import React from 'react';
import CardUsageOptions from 'features/cards/components/card-usage-options/card-usage-options';
import dayjs from 'dayjs';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import DeprecatedIcon from '../../../../assets/svg-icons';
import ConfirmExitModal from 'features/cards/modals/confirm-exit-modal/confirm-exit-modal';
import AcknowledgeModal from 'features/cards/modals/acknowledge-modal/acknowledge-modal';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';
import Label from 'constants/labels';
import isEmpty from 'utilities/isEmpty';
import Vas from 'constants/vas-codes';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';
import './add-edit-card-form.scss';

import { Form, FormValidationMode } from '@ampol/design-system';
import { AUSSIE_STATES, DATE_FORMATS, RENEWAL_OPTIONS, addressValidationSchema } from 'utilities/constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectAddEditCardFormContext } from 'selectors/addEditCardFormSelectors';
import { createCard, editCardDetails } from 'actions/cardActions';
import * as yup from 'yup';
import {
  getAddEditCardRequest,
  getCardUsageOptionsSchema,
  getFavouritesDetails,
  getRequiredVasSchema,
  isReissueForm,
  isEvNewlyAdded,
  cardHasRfid,
} from 'features/cards/helpers/add-edit-card-helper';
import { getAccountOptions } from 'utilities/account-utilities';
import { isDateBetween } from 'utilities/dayjs-utilities';
import { useCheckGetAllAccountsRequest } from 'components/use-get-all-accounts-request/use-get-all-accounts-request';
import { trackCustomEvent } from 'utilities/app-insights';
import { useReissueFlow } from 'features/cards/hooks/use-reissue-flow';
import Env from 'Env';
import { useAddEditCardDefaults } from './useAddEditCardDefaults';
import type { CardDetail } from './useAddEditCardDefaults';
import {
  capDailyLimitToCreditLimit,
  capDailyLimitToMaximumOption,
  filterDailyDollarLimitsByCreditLimit,
  filterRestrictedDailyDollarLimits,
  getEffectiveCreditLimit,
} from 'features/cards/helpers/card-limit-utilities';

dayjs.extend(utc);
dayjs.extend(timezone);

type AddEditCardFormProps = {
  cardDetails?: CardDetail;
  onClose: () => void;
  onEvReissueComplete?: () => void;
  formId?: string;
  isEditMultipleCard?: boolean;
  onCardChanges?: (_request: AddEditCardFormValues) => void;
  setIsFormValid?: (_isValid: boolean) => void;
  setModifiedCardDetails?: (_values: AddEditCardFormValues) => void;
};

type FleetLimiterOption = {
  code: string;
  available: boolean;
  selected?: boolean;
};

type AccountFavouriteOption = {
  id: string;
  [key: string]: unknown;
};

type AddEditCardFormValues = Record<string, any>;

const AddEditCardForm: React.FC<AddEditCardFormProps> = ({
  cardDetails = {},
  onClose,
  onEvReissueComplete,
  formId,
  isEditMultipleCard,
  onCardChanges,
  setIsFormValid,
  setModifiedCardDetails,
}) => {
  const dispatch = useDispatch();
  const isEvWay4Enabled = typeof Env.getIsEvWay4Enabled === 'function' ? Env.getIsEvWay4Enabled() : false;
  formId = formId ?? 'card-edit-form';
  const formType = !isEmpty(cardDetails) ? 'edit' : 'add';
  const {
    allAccounts,
    allAccountsIsLoading,
    accountDefaults,
    assignedGrandfatherClientNumber,
    loggedInUser,
    assignedAccountNumber,
    cardOptions,
    cardOptionsAccountNumber,
    cardOptionsLoading,
    vehicleTypes,
    plasticType,
  } = useSelector(selectAddEditCardFormContext);

  useCheckGetAllAccountsRequest({ enabled: formType === 'add' });

  const accountOptions = getAccountOptions(allAccounts, formType === 'edit');

  const minExpiryMonths = 1;
  const maxExpiryMonths = formType === 'edit' ? 99 : 60;

  const {
    showReissueModal,
    showEvAcknowledgeModal,
    initiateReissue,
    confirmReissue,
    acknowledgeEvReissue,
    cancelReissue,
  } = useReissueFlow({
    assignedGrandfatherClientNumber: assignedGrandfatherClientNumber ?? '',
    loggedInUser,
    assignedAccountNumber: assignedAccountNumber ?? '',
    onClose,
    onEvReissueComplete,
  });

  const {
    defaultValues,
    setDefaultValues,
    fleetLimitersRef,
    cardOptionsRef,
    accountFavouritesData,
    vasRsaAccountCode,
    vasSnrAccountCode,
    vasFuelPayAccountCode,
    handleAccountSelection,
  } = useAddEditCardDefaults({
    cardDetails,
    allAccounts,
    accountDefaults,
    cardOptions,
    cardOptionsAccountNumber,
    vehicleTypes,
    plasticType,
    formType,
    formId,
    isEditMultipleCard,
    assignedGrandfatherClientNumber: assignedGrandfatherClientNumber ?? '',
    maxExpiryMonths,
    setModifiedCardDetails,
  });
  const selectedContractNumber = formType === 'edit' ? cardDetails?.contractNumber : defaultValues?.contractNumber;
  const accountCreditLimit = getEffectiveCreditLimit(allAccounts, selectedContractNumber);
  const dailyDollarLimitsOptions =
    formType === 'edit' ? cardDetails?.dailyDollarLimits : accountDefaults?.dailyDollarLimits;
  const shouldFilterDailyLimits = typeof Env.getIsCardLimitsReadOnly === 'function' && Env.getIsCardLimitsReadOnly();
  const allowedDailyDollarLimits = filterRestrictedDailyDollarLimits(dailyDollarLimitsOptions);
  const availableDailyDollarLimits = shouldFilterDailyLimits
    ? filterDailyDollarLimitsByCreditLimit(allowedDailyDollarLimits, accountCreditLimit)
    : allowedDailyDollarLimits;

  const getFleetLimiters = () => {
    if (formType === 'add' && accountDefaults?.fleetLimiters) {
      return accountDefaults?.fleetLimiters.map((option: FleetLimiterOption) => option.code);
    } else if (formType === 'edit' && cardDetails?.fleetLimiters) {
      return cardDetails?.fleetLimiters.map((option: FleetLimiterOption) => option.code);
    }

    return [];
  };

  let validSchema = yup.object().shape({
    // Card Details Validation
    contractNumber: yup.string().required('Please select an account number.'),
    embossedLastName: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .required(Validation.EMBOSSING_NAME_REQUIRED)
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL, Validation.ALPHA_NUM_SPACE_SPECIAL)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    embossedCompanyName: yup
      .string()
      .transform((value) => (value ? value.trim() : value))
      .nullable()
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL2, Validation.ALPHA_NUM_SPACE_SPECIAL2)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    expiryDate: yup
      .string()
      .required(Validation.EXPIRY_DATE_REQUIRED)
      .matches(Regex.EXPIRY_MONTH_YEAR, Validation.EXPIRY_DATE_MONTH_YEAR)
      .test(
        'expiryDate',
        `Must be between ${dayjs()
          .tz('Australia/Sydney')
          .add(minExpiryMonths + 1, 'month')
          .endOf('month')
          .format(
            'MM/YY'
          )} to ${dayjs().tz('Australia/Sydney').add(maxExpiryMonths, 'months').startOf('month').format('MM/YY')}`,
        (value) => {
          if (!value || !value.match(Regex.EXPIRY_MONTH_YEAR)) {
            return true;
          }
          const inputDate = dayjs(`01/${value}`, DATE_FORMATS).tz('Australia/Sydney');

          const minDate = dayjs().add(minExpiryMonths, 'month').endOf('month').tz('Australia/Sydney');
          const maxDate = dayjs()
            .add(maxExpiryMonths + 1, 'months')
            .startOf('month')
            .tz('Australia/Sydney');

          return isDateBetween(inputDate, minDate, maxDate);
        }
      ),
    costCentre: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE_HYPHEN, Validation.ALPHA_NUM_SPACE_HYPHEN)
      .max(36, Validation.MAX_CHAR_THIRTY_SIX),
    driverName: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.DRIVER_NAME, Validation.DRIVER_NAME)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    domesticNumber: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .max(16, Validation.MAX_CHAR_SIXTEEN),
    // Required fields if roadsideAssist or serviceAndRepairs checkboxes are ticked
    ...getRequiredVasSchema(),
    // Delivery Address Validation
    contactFirstName: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER)
      .max(19, Validation.MAX_CHAR_NINETEEN),
    contactLastName: yup
      .string()
      .nullable()
      .transform((value) => (value ? value.trim() : value))
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER)
      .max(19, Validation.MAX_CHAR_NINETEEN),
  });

  validSchema = validSchema.concat(addressValidationSchema).concat(getCardUsageOptionsSchema(getFleetLimiters()));

  const handleSubmit = (values: AddEditCardFormValues) => {
    const request = getAddEditCardRequest(values, fleetLimitersRef.current, assignedGrandfatherClientNumber);
    if (formType === 'add') {
      dispatch(createCard(request));
      trackCustomEvent(
        'Submit Add New Card',
        loggedInUser,
        assignedAccountNumber,
        'Clicked Submit to submit Add New Card.',
        { selectedAccount: request?.contractNumber }
      );
      onClose();
    } else if (formType === 'edit' && !isEditMultipleCard && formId === 'card-edit-single-status') {
      onCardChanges?.(request);
      onClose();
    } else if (formType === 'edit' && !isEditMultipleCard) {
      const evSelected = isEvNewlyAdded(values, cardDetails);
      const rfidPresent = cardHasRfid(cardDetails);

      if (isEvWay4Enabled && evSelected && !rfidPresent) {
        initiateReissue(request, true);
      } else {
        const isReissue = isReissueForm(cardDetails, values);

        if (isReissue) {
          initiateReissue(request);
        } else {
          dispatch(editCardDetails(request, assignedGrandfatherClientNumber));
          trackCustomEvent(
            'Submit Edit Card',
            loggedInUser,
            assignedAccountNumber,
            'Clicked Submit to submit Edit Card.',
            { selectedAccount: request?.contractNumber, selectedCard: request?.contractIdentifier }
          );
          onClose();
        }
      }
    } else if (formType === 'edit' && isEditMultipleCard) {
      onCardChanges?.(request);
    }
  };

  const handleChange = (values: AddEditCardFormValues, name: string | undefined) => {
    if (name === 'allProducts') {
      setDefaultValues({
        ...values,
        ...fleetLimitersRef?.current?.reduce(
          (acc: Record<string, boolean>, option: FleetLimiterOption) => ({
            ...acc,
            [option.code]: option.available ? values.allProducts : false,
          }),
          {} as Record<string, boolean>
        ),
      });
    } else if (name === 'contractNumber') {
      setDefaultValues({
        ...values,
      });
      handleAccountSelection(values.contractNumber);
    } else if (name === 'accountFavorites') {
      const favSelected = cardOptionsRef.current.find(
        (obj: AccountFavouriteOption) => obj.id === values.accountFavorites
      );

      window.dataLayer.push({
        event: 'Card_Portal_Add_New_Card_Card_Usage_Favourite',
        favourite: favSelected,
      });

      const favouriteDetails = getFavouritesDetails(
        favSelected,
        fleetLimitersRef.current,
        vasRsaAccountCode,
        vasSnrAccountCode
      );
      setDefaultValues({
        ...values,
        ...favouriteDetails,
        dailyLimit: capDailyLimitToMaximumOption(
          shouldFilterDailyLimits
            ? capDailyLimitToCreditLimit(favouriteDetails.dailyLimit, allowedDailyDollarLimits, accountCreditLimit)
            : favouriteDetails.dailyLimit,
          availableDailyDollarLimits
        ),
      });
    } else {
      // Check whether you should toggle the allproducts checkbox based on individual products selection
      const availableProductCount = fleetLimitersRef?.current?.filter((option: FleetLimiterOption) => option.available);
      const selectedProductCount = fleetLimitersRef?.current?.filter(
        (option: FleetLimiterOption) => option.available && values[option.code]
      );

      if (availableProductCount && availableProductCount?.length > 0) {
        if (selectedProductCount && availableProductCount?.length === selectedProductCount?.length) {
          values = { ...values, allProducts: true };
        } else {
          values = { ...values, allProducts: false };
        }
      }
      setDefaultValues({
        ...values,
      });

      if (name && 'embossedLastName,embossedCompanyName,registrationNumber,expiryDate'.includes(name))
        setModifiedCardDetails?.(values);
    }
  };

  const handleStateChange = ({ isValid }: { isValid: boolean }) => {
    setIsFormValid?.(isValid);
  };

  // Cast to any to bypass prop mismatch between JSX callers and component definition
  const CardUsageOptionsAny: any = CardUsageOptions;

  return (
    <div className={'add-edit-card-form'}>
      {isEvWay4Enabled && showEvAcknowledgeModal && (
        <AcknowledgeModal
          title='Card Reissue'
          content={Label.EV_CHARGING_REISSUE_NOTIFICATION}
          onClose={acknowledgeEvReissue}
        />
      )}
      {showReissueModal && (
        <ConfirmExitModal
          title={Label.EDIT_CARD}
          content={'You have made changes that will cause a reissue. Are you sure you want to continue?'}
          submitLabel={'Confirm'}
          onConfirmExitClick={confirmReissue}
          onClose={cancelReissue}
        />
      )}
      <Form
        id={formId}
        schema={validSchema}
        defaultValues={defaultValues}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onStateChange={handleStateChange}
        validationMode={FormValidationMode.onChange}
      >
        <div
          className={`${'add-edit-card-form__section'}${isEditMultipleCard ? 'add-edit-card-form__accountdropdown' : ''}`}
        >
          <h3>Account</h3>
          <Form.DropdownSearch
            label={'Account'}
            name={'contractNumber'}
            data={accountOptions}
            placeholder={'Select Account'}
            allowClear={false}
            loading={allAccountsIsLoading}
            disabled={formType === 'edit'}
          />
        </div>
        <div className={'add-edit-card-form__section'}>
          <h3>Card details</h3>
          <Form.Input label={'Embossing Name'} name={'embossedLastName'} className={'add-edit-card-form__uppercase'} />
          <Form.Input
            label={'Embossing Line 2'}
            name={'embossedCompanyName'}
            className={'add-edit-card-form__uppercase'}
          />
          <Form.Input
            label={'Vehicle Registration'}
            name={'registrationNumber'}
            className={'add-edit-card-form__uppercase'}
          />
          <div className={'add-edit-card-form__split-col'}>
            <Form.Input label={'Issue Date'} name={'issueDate'} disabled={true} />
            <Form.DatePicker
              label={'Expiry Date (MM/YY)'}
              name={'expiryDate'}
              placeholder={'MM/YY'}
              format={'MM/YY'}
              picker={'month'}
              getPopupContainer={(trigger: any) => (isEditMultipleCard ? document.body : trigger.parentElement)}
              inputSuffix={
                <SuffixWrapper>
                  <DeprecatedIcon iconName='calender' />
                </SuffixWrapper>
              }
            />
          </div>
          <Form.DropdownSearch
            label={'Renewal Option'}
            name={'renewalOption'}
            type={'dropdown'}
            data={RENEWAL_OPTIONS}
            placeholder={'Select Renewal Option'}
          />
          <Form.Input label={'Cost Centre Code'} name={'costCentre'} className={'add-edit-card-form__uppercase'} />
          <div className={'add-edit-card-form__full'}>
            <Form.Checkbox
              label={'Enable Fuel Pay via Ampol App'}
              name={'fuelPay'}
              color={'blue'}
              disabled={vasFuelPayAccountCode === Vas.FP_NC}
            />
          </div>
          <Form.Input label={'Driver Name'} name={'driverName'} className={'add-edit-card-form__uppercase'} />
          <Form.Input label={'Domestic Number'} name={'domesticNumber'} className={'add-edit-card-form__uppercase'} />
          <Form.DropdownSearch
            label={'Registration State'}
            name={'registrationState'}
            type={'dropdown'}
            data={AUSSIE_STATES}
            placeholder={'Select Registration State'}
          />
          <div className={'add-edit-card-form__full'}>
            <Form.Input
              label={'Vehicle Description'}
              name={'vehicleDescription'}
              className={'add-edit-card-form__uppercase'}
            />
          </div>
        </div>
        <div className={'add-edit-card-form__section'}>
          <h3>Delivery address</h3>
          <Form.Input label={'Contact First Name'} name={'contactFirstName'} />
          <Form.Input label={'Contact Last Name'} name={'contactLastName'} />
          <Form.Input label={'Address Line 1'} name={'addressLine1'} />
          <Form.Input label={'Address Line 2'} name={'addressLine2'} />
          <Form.Input label={'City'} name={'city'} />
          <Form.DropdownSearch
            label={'State'}
            name={'state'}
            type={'dropdown'}
            data={AUSSIE_STATES}
            placeholder={'Select State'}
          />
          <Form.Input label={'Postcode'} name={'postcode'} />
        </div>
        <div className={'add-edit-card-form__section'}>
          <h3>Card usage options</h3>
          <Form.DropdownSearch
            label={'Select Favourites'}
            name={'accountFavorites'}
            data={accountFavouritesData}
            allowClear={false}
            loading={cardOptionsLoading}
            placeholder={'Select Favourite'}
          />
          <CardUsageOptionsAny
            limitMode={formType}
            fleetLimiterOptions={fleetLimitersRef.current}
            isCardProcessing={cardDetails?.cardStatus === 'Processing'}
            monthlyDollarLimitsOptions={
              formType === 'edit' ? cardDetails?.monthlyDollarLimits : accountDefaults?.monthlyDollarLimits
            }
            dailyDollarLimitsOptions={dailyDollarLimitsOptions}
            accountCreditLimit={accountCreditLimit}
            vehicleTypesOptions={vehicleTypes}
            vasRsaAccountCode={vasRsaAccountCode}
            vasSnrAccountCode={vasSnrAccountCode}
            formValues={defaultValues}
            isEditMultipleCard={isEditMultipleCard}
            servicesAndRepairsFee={
              formType === 'edit'
                ? cardDetails?.accountDefault?.vasServicesAndRepairsFee
                : accountDefaults?.vasServicesAndRepairsFee
            }
            roadSideAssistFeeHeavy={
              formType === 'edit'
                ? cardDetails?.accountDefault?.vasRoadSideAssistFeeHeavy
                : accountDefaults?.vasRoadSideAssistFeeHeavy
            }
            roadSideAssistFeePassenger={
              formType === 'edit'
                ? cardDetails?.accountDefault?.vasRoadSideAssistFeePassenger
                : accountDefaults?.vasRoadSideAssistFeePassenger
            }
            roadSideAssistFeeTruck={
              formType === 'edit'
                ? cardDetails?.accountDefault?.vasRoadSideAssistFeeTruck
                : accountDefaults?.vasRoadSideAssistFeeTruck
            }
          />
        </div>
      </Form>
    </div>
  );
};

export default AddEditCardForm;
